import { CheckIcon, CopyIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { cn } from '@w/ui/lib/utils'
import { SearchableSelect } from '@w/ui/components/searchable-select'
import type { CodeHighlightingProps, CodeHighlightingControlsProps } from './types'
export type { CodeHighlightingProps, CodeHighlightingControlsProps } from './types'
import { useEffect, useState } from 'react'
import { tokenizeCode, type CodeToken } from './highlight-code-runs'
import { codeHighlightingLanguages, LanguageIcon } from './language-icon'
import {
  codeHighlightingSurfaceClassName,
  codeHighlightingSurfaceTokens,
} from './code-theme'

const codeHighlightingTokenClassNames = {
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
  success: 'text-success',
  primary: 'text-primary',
  muted: 'text-on-surface-secondary',
} as const

export { codeHighlightingLanguages } from './language-icon'

export function CodeHighlighting({
  code,
  language,
  defaultLanguage = 'text',
  languages = codeHighlightingLanguages,
  onLanguageChange,
  allowLanguageSelection = true,
  showCopyButton = true,
  controlsClassName,
  className,
  ...props
}: CodeHighlightingProps) {
  const [uncontrolledLanguage, setUncontrolledLanguage] =
    useState(defaultLanguage)
  const activeLanguage = language ?? uncontrolledLanguage
  const [tokenized, setTokenized] = useState<{
    code: string
    language: string
    tokens: CodeToken[]
  }>()

  useEffect(() => {
    let active = true
    async function loadTokens() {
      const nextTokens = await tokenizeCode(code, activeLanguage)
      if (active) {
        setTokenized({ code, language: activeLanguage, tokens: nextTokens })
      }
    }
    void loadTokens()
    return () => {
      active = false
    }
  }, [activeLanguage, code])

  const tokens =
    tokenized?.code === code && tokenized.language === activeLanguage
      ? tokenized.tokens
      : [{ text: code }]
  const tokensWithOffsets = _withOffsets(tokens)

  function changeLanguage(nextLanguage: string | null) {
    if (nextLanguage === null) return
    if (language === undefined) setUncontrolledLanguage(nextLanguage)
    onLanguageChange?.(nextLanguage)
  }

  return (
    <div
      className={cn('group text-on-surface relative min-w-0', className)}
      data-empty={code.length === 0}
      {...props}
    >
      <CodeHighlightingControls
        allowLanguageSelection={allowLanguageSelection}
        code={code}
        controlsClassName={controlsClassName}
        language={activeLanguage}
        languages={languages}
        onLanguageChange={changeLanguage}
        showCopyButton={showCopyButton}
      />
      <pre className={codeHighlightingSurfaceClassName}>
        {code.length === 0 ? (
          <code className="text-on-surface-secondary font-sans italic">
            Empty code block
          </code>
        ) : (
          <code className="font-inherit whitespace-pre">
            {tokensWithOffsets.map((token) => (
              <span
                className={
                  token.tone
                    ? codeHighlightingTokenClassNames[token.tone]
                    : undefined
                }
                key={token.offset}
              >
                {token.text}
              </span>
            ))}
          </code>
        )}
      </pre>
    </div>
  )
}

export function CodeHighlightingControls({
  code,
  language,
  languages = codeHighlightingLanguages,
  onLanguageChange,
  allowLanguageSelection = true,
  showCopyButton = true,
  controlsClassName,
}: CodeHighlightingControlsProps) {
  const [copied, setCopied] = useState(false)

  function copy() {
    void copyToClipboard()
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(typeof code === 'function' ? code() : code)
    setCopied(true)
    window.setTimeout(() => {
      setCopied(false)
    }, 2_000)
  }

  return (
    <>
      <div className="absolute top-0 left-0 z-10 -translate-x-4 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:opacity-100">
        {allowLanguageSelection ? (
          <SearchableSelect
            emptyLabel="No languages found"
            minimal
            onValueChange={(value) => {
              if (value !== null) onLanguageChange?.(value)
            }}
            options={languages.map((item) => ({
              icon: <LanguageIcon language={item} />,
              label: languageLabel(item),
              searchText: languageSearchText(item),
              value: item,
            }))}
            searchPlaceholder="Search languages…"
            className={cn(
              codeHighlightingSurfaceTokens.controlsClassName,
              controlsClassName,
            )}
            triggerIcon={<LanguageIcon language={language} />}
            triggerVariant="icon"
            value={language}
          />
        ) : (
          <span
            className={cn(
              codeHighlightingSurfaceTokens.controlsClassName,
              'flex size-10 items-center justify-center rounded-full p-2.5',
              controlsClassName,
            )}
          >
            <LanguageIcon language={language} />
            <span className="sr-only">{languageLabel(language)}</span>
          </span>
        )}
      </div>
      {showCopyButton ? (
        <div className="absolute top-0 right-0 z-10 translate-x-4 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          <IconButton
            className={cn(
              codeHighlightingSurfaceTokens.controlsClassName,
              'text-on-surface-secondary hover:text-on-surface',
              controlsClassName,
            )}
            onClick={copy}
            type="button"
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? 'Copied' : 'Copy code'}
          </IconButton>
        </div>
      ) : null}
    </>
  )
}

function languageLabel(language: string) {
  const labels: Record<string, string> = {
    bash: 'Bash',
    c: 'C',
    cpp: 'C++',
    csharp: 'C#',
    css: 'CSS',
    dart: 'Dart',
    diff: 'Diff',
    dockerfile: 'Dockerfile',
    elixir: 'Elixir',
    erlang: 'Erlang',
    go: 'Go',
    graphql: 'GraphQL',
    groovy: 'Groovy',
    html: 'HTML',
    ini: 'INI / TOML',
    java: 'Java',
    javascript: 'JavaScript',
    json: 'JSON',
    kotlin: 'Kotlin',
    less: 'Less',
    lua: 'Lua',
    makefile: 'Makefile',
    markdown: 'Markdown',
    nginx: 'Nginx',
    objectivec: 'Objective-C',
    perl: 'Perl',
    php: 'PHP',
    powershell: 'PowerShell',
    python: 'Python',
    r: 'R',
    ruby: 'Ruby',
    rust: 'Rust',
    scala: 'Scala',
    scss: 'SCSS',
    sql: 'SQL',
    swift: 'Swift',
    text: 'Plain text',
    typescript: 'TypeScript',
    vbnet: 'Visual Basic .NET',
    wasm: 'WebAssembly',
    yaml: 'YAML',
  }
  return labels[language] ?? language
}

function languageSearchText(language: string) {
  const aliases: Record<string, string> = {
    bash: 'shell sh',
    cpp: 'c++',
    csharp: 'c# cs',
    elixir: 'ex exs',
    html: 'xml',
    ini: 'toml',
    javascript: 'js jsx',
    kotlin: 'kt kts',
    objectivec: 'objc',
    powershell: 'ps1',
    python: 'py',
    ruby: 'rb',
    rust: 'rs',
    text: 'plain plaintext txt',
    typescript: 'ts tsx',
    yaml: 'yml',
  }
  return `${languageLabel(language)} ${language} ${aliases[language] ?? ''}`
}

function _withOffsets(tokens: CodeToken[]) {
  let offset = 0
  return tokens.map((token) => {
    const tokenWithOffset = { ...token, offset }
    offset += token.text.length
    return tokenWithOffset
  })
}
